import { NavLink } from 'react-router-dom';
import { useProject } from '../hooks/useProject';
import { can, ROLE_LABELS, type RoleAction } from '../lib/roles';

/**
 * Primary navigation. `NavLink` handles the active state, so the route table
 * in routes.tsx stays the only place paths are declared.
 *
 * Links carry an optional role action: a link is hidden when the active
 * project's role can't use the screen at all (keys/webhooks for treasurer and
 * operator). Read-only screens stay visible to every role.
 */
const LINKS: Array<{ to: string; label: string; end?: boolean; action?: RoleAction }> = [
  { to: '/', label: 'Overview', end: true },
  { to: '/wallet', label: 'Wallet' },
  { to: '/fiat', label: 'Fiat account' },
  { to: '/usd', label: 'USD & payouts' },
  { to: '/rates', label: 'Rates' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/onramps', label: 'Onramps' },
  { to: '/offramps', label: 'Offramps' },
  { to: '/owed-offramps', label: 'Owed offramps', action: 'move_money' },
  { to: '/failed-onramps', label: 'Failed onramps', action: 'move_money' },
  { to: '/counterparties', label: 'Counterparties' },
  { to: '/ledger', label: 'Ledger' },
  { to: '/api-keys', label: 'API keys', action: 'manage_keys' },
  { to: '/webhooks', label: 'Webhooks', action: 'manage_keys' },
  { to: '/logs', label: 'Request logs' },
  { to: '/team', label: 'Team' },
  { to: '/projects', label: 'Projects' },
  { to: '/docs', label: 'Documentation' },
  { to: '/settings', label: 'Settings' },
];

export function Sidebar() {
  const { project, projects, switchProject } = useProject();

  const links = LINKS.filter((link) => !link.action || can(project?.role, link.action));

  return (
    <nav className="sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-mark">D</span>
        <span>Difference</span>
      </div>

      {projects.length > 0 && (
        <div style={{ padding: '4px 10px 12px' }}>
          <select
            aria-label="Active project"
            value={project?.id ?? ''}
            onChange={(event) => switchProject(event.target.value)}
            style={{ width: '100%' }}
          >
            {projects.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.organization_name} / {candidate.name}
              </option>
            ))}
          </select>
          {project && (
            <div className="muted" style={{ fontSize: 12, marginTop: 6, paddingLeft: 2 }}>
              Your role: {ROLE_LABELS[project.role]}
            </div>
          )}
        </div>
      )}

      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.end}
          className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
        >
          {link.label}
        </NavLink>
      ))}

      <div className="sidebar-footer">v0.1.0</div>
    </nav>
  );
}
